/**
 * Client-side deadlines for the proxy's NDJSON streams (/turn/stream, /tts/stream).
 *
 * A network that buffers the streamed response until the server ends it (or never
 * delivers it) leaves fetch() and reader.read() waiting indefinitely: the learner sat
 * on a locked mic until the 60s recording watchdog, and the turn was lost. These
 * deadlines turn that silence into an ordinary pre-audio failure (legacy fallback) or
 * post-audio failure (commit the partial turn), exactly like any other stream error.
 */

// Before flushing headers + meta the proxy does no LLM or TTS work — only load
// shedding, Firebase token verification and parsing the body. What can delay meta is
// the network: CORS preflight + TLS, a Cloud Run cold start (~2-5s), and uploading a
// long recording's base64 body (up to ~1MB) on a slow uplink. Healthy clients see
// meta ~100ms after the call; a false positive only moves that learner to the legacy
// path, so this errs generous.
export const STREAM_FIRST_EVENT_TIMEOUT_MS = 8000;

// Must exceed the server's longest legitimate silence between events: the LLM
// inter-chunk stall guard (30s, handlers/turnStream.js DEFAULT_LLM_CHUNK_GAP_MS) and
// the per-sentence Neural2 synthesis cap (22s, utils/neuralSentences.js
// SYNTH_TIMEOUT_MS). The pre-transcript gap is usually a few seconds, but the first
// turn of a new activity can spend up to ~25s generating its prompt before the LLM
// even starts, so this deadline can fire on a healthy network. That only costs one
// legacy re-run: the stream had already delivered events, so it never turns
// streaming off (see deliveredAnyEvent).
export const STREAM_IDLE_TIMEOUT_MS = 35000;

function abortError() {
  const error = new Error('The stream was aborted');
  error.name = 'AbortError';
  return error;
}

/**
 * One deadline per stream: `first_event_timeout` from start() until the first parsed
 * event, then `idle_timeout` re-armed by every event. Expiry rejects whatever guard()
 * is currently racing, so the stream fails even if fetch or the body reader ignores
 * the abort.
 *
 * @param {Object} options
 * @param {AbortSignal} options.signal - The stream's own signal; events arriving after
 *   it aborted are refused so a late buffered reply can never play.
 * @param {number} [options.firstEventTimeoutMs]
 * @param {number} [options.idleTimeoutMs]
 */
export function createStreamDeadlines({
  signal,
  firstEventTimeoutMs = STREAM_FIRST_EVENT_TIMEOUT_MS,
  idleTimeoutMs = STREAM_IDLE_TIMEOUT_MS,
} = {}) {
  let timer = null;
  let sawEvent = false;
  let expiredReason = null;
  let handlerError = null;
  let networkError = null;
  let rejectExpiry;
  const expiry = new Promise((resolve, reject) => { rejectExpiry = reject; });
  // Observed only through guard(); an expiry nobody is racing is not an unhandled rejection.
  expiry.catch(() => {});

  // A deliberate stop (stopStreamingPlayback) must surface as AbortError right away,
  // even if fetch or the reader ignores the signal — and must never be followed by a
  // deadline expiry that would turn a dropped turn into a legacy re-run.
  if (signal && typeof signal.addEventListener === 'function') {
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      timer = null;
      rejectExpiry(abortError());
    }, { once: true });
  }

  const arm = (ms, reason) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      expiredReason = reason;
      rejectExpiry(new Error(`Stream ${reason} after ${ms}ms`));
    }, ms);
  };

  return {
    /** Arm the first-event deadline. Call immediately before fetch(). */
    start() {
      arm(firstEventTimeoutMs, 'first_event_timeout');
    },

    /**
     * Race a fetch or read promise against the current deadline. A TypeError it
     * rejects with is how fetch reports a network failure — unless the event handler
     * threw it, which says nothing about the network.
     */
    guard(promise) {
      const tagged = promise.catch((error) => {
        if (error && error.name === 'TypeError' && error !== handlerError) networkError = error;
        throw error;
      });
      return Promise.race([tagged, expiry]);
    },

    /**
     * Wrap the per-event handler: each event re-arms the idle deadline, and errors the
     * handler itself throws are remembered so they are never mistaken for the network.
     */
    wrapHandler(handler) {
      return (event) => {
        if (expiredReason || (signal && signal.aborted)) throw abortError();
        sawEvent = true;
        arm(idleTimeoutMs, 'idle_timeout');
        try {
          handler(event);
        } catch (error) {
          handlerError = error;
          throw error;
        }
      };
    },

    /**
     * Whether at least one event reached the client. A network that delivered one can
     * carry the stream, so a later stall or drop fails that turn only; a network that
     * delivered nothing (buffered, held back, host blocked) is the one to stop using.
     * @returns {boolean}
     */
    deliveredAnyEvent() {
      return sawEvent;
    },

    /** Disarm. Idempotent; call once the stream has settled either way. */
    stop() {
      clearTimeout(timer);
      timer = null;
    },

    /**
     * The network-shaped reason behind a stream failure, or null: a deadline expiry,
     * or a network TypeError from a guarded fetch/read. Never an error thrown while
     * handling an event, an HTTP status, a server error event, or a deliberate abort.
     * @param {*} error
     * @returns {'first_event_timeout'|'idle_timeout'|'network_error'|null}
     */
    networkFailureReason(error) {
      if (expiredReason) return expiredReason;
      if (error && error === networkError) return 'network_error';
      return null;
    },
  };
}
